import { SessionManager } from "sip.js/lib/platform/web";
import { SimpleUser } from "sip.js/lib/platform/web";

/**
 * A simple SIP user class.
 * @remarks
 * While this class is completely functional for simple use cases, it is not intended
 * to provide an interface which is suitable for most (must less all) applications.
 * While this class has many limitations (for example, it only handles a single concurrent session),
 * it is, however, intended to serve as a simple example of using the SIP.js API.
 * @public
 */
export class User extends SimpleUser {
  delegate;

  logger;
  options;
  session = undefined;
  sessionManager;
  /**
   * Constructs a new instance of the `SimpleUser` class.
   * @param server - SIP WebSocket Server URL.
   * @param options - Options bucket. See {@link SimpleUserOptions} for details.
   */
  constructor(server, options) {
    super(server, options);
    // Delegate
    this.delegate = options.delegate;

    // Copy options
    this.options = { ...options };

    // Session manager options
    const sessionManagerOptions = {
      aor: this.options.aor,
      autoStop: true,
      delegate: {
        onCallAnswered: (session) => this.delegate?.onCallAnswered?.(session),
        onCallCreated: (session) => {
          this.session = session;
          this.delegate?.onCallCreated?.(session);
        },
        onCallReceived: (invitation) =>
          this.delegate?.onCallReceived?.(invitation),
        onCallHangup: () => {
          this.session = undefined;
          this.delegate?.onCallHangup && this.delegate?.onCallHangup();
        },
        onCallHold: (s, held) => this.delegate?.onCallHold?.(held),
        onCallDTMFReceived: (s, tone, dur) =>
          this.delegate?.onCallDTMFReceived?.(tone, dur),
        onMessageReceived: (message) =>
          this.delegate?.onMessageReceived?.(message.request.body),
        onRegistered: () => this.delegate?.onRegistered?.(),
        onUnregistered: () => this.delegate?.onUnregistered?.(),
        onServerConnect: () => this.delegate?.onServerConnect?.(),
        onServerDisconnect: () => this.delegate?.onServerDisconnect?.(),
      },
      maxSimultaneousSessions: 1,
      media: this.options.media,
      reconnectionAttempts: this.options.reconnectionAttempts,
      reconnectionDelay: this.options.reconnectionDelay,
      registererOptions: this.options.registererOptions,
      sendDTMFUsingSessionDescriptionHandler:
        this.options.sendDTMFUsingSessionDescriptionHandler,
      userAgentOptions: this.options.userAgentOptions,
    };

    this.sessionManager = new SessionManager(server, sessionManagerOptions);

    // Use the SIP.js logger
    this.logger = this.sessionManager.userAgent.getLogger("sip.SimpleUser");
  }

  /**
   * Hangup a call.
   * @remarks
   * Send a BYE request, CANCEL request or reject response to end the current Session.
   * Resolves when the request/response is sent, otherwise rejects.
   * Use `onCallHangup` delegate method to determine if and when call is ended.
   */
  hangup() {
    this.logger.log(`[${this.id}] Hangup...`);
    if (!this.session) {
      return Promise.reject(new Error("Session does not exist."));
    }
    console.log(this.session);
    return this.sessionManager.hangup(this.session).then(() => {
      this.session = undefined;
    });
  }
}
